import React from 'react';
import PropTypes from 'prop-types';
import { CHILDREN_PROP_TYPES } from 'propTypes';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/system';
import { InView } from 'react-intersection-observer';

import PhoneAnchor from 'bento-ordering/base/components/PhoneAnchor';
import AddressLink from 'bento-ordering/base/components/AddressLink';
import LocationsPaper from 'bento-ordering/locations/ui_components/LocationsPaper';
import StaticMapComponent from 'bento-ordering/base/components/StaticMapComponent';
import Cover from 'bento-ordering/menus/ui_components/Cover';
import Flex from 'bento-ordering/base/ui_components/Flex';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import MenuItemNameTypography from 'components/MenuItemNameTypography';
import {
  getMenuItemNamesMediumColor,
  getSectionNavLighterColor
} from 'styles/helpers/getThemeStyles';
import ownCardState from 'helpers/ownState/DropShadowState';

import { baseCoverSx } from 'styles/sx/coverSx';
import {
  ITEM_DESCRIPTION_AND_PRICES,
  MENU_ITEM_NAMES
} from 'styles/constants/appearanceSectionNames';

const MAP_BOX_MIN_WIDTH = '146px';
const MAP_BOX_MIN_HEIGHT = '136px';

const StyledMap = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '50px',
  minWidth: MAP_BOX_MIN_WIDTH,
  [theme.breakpoints.up('xs')]: {
    minHeight: MAP_BOX_MIN_HEIGHT
  }
}));

export const ownTextState = {
  text: theme => ({
    color: getMenuItemNamesMediumColor(theme)
  })
};

const StyledCardActionsSection = styled(Box)(({ theme, isCatering }) => {
  const borderColor = getSectionNavLighterColor(theme);
  return {
    display: 'block',
    [theme.breakpoints.up('md')]: {
      borderLeft: `1px solid ${borderColor}`,
      paddingLeft: '20px',
      display: 'flex',
      justifyContent: isCatering ? 'space-evenly' : 'space-between',
      flexDirection: isCatering ? 'column' : 'row',
      alignItems: isCatering ? 'center' : 'stretch'
    }
  };
});

const StyledLocationInfo = styled(Box)({
  flexGrow: 2
});

const StyledLocationCard = styled(CardContent)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexGrow: 1,
    height: '100%'
  }
}));

const StyledMapCover = styled(Cover)(({ theme }) => ({
  ...baseCoverSx,
  [theme.breakpoints.down('lg')]: {
    position: 'relative'
  }
}));

const InterSectionObserver = styled(Box)(({ minHeight }) => ({
  width: '100%',
  height: '100%',
  minHeight
}));

const LocationLabelTypography = styled(MenuItemNameTypography)({
  fontSize: '20px'
});

const LocationsCard = props => {
  const {
    name,
    description,
    distance,
    street,
    city,
    state,
    postalCode,
    phoneNumber,
    googlePlaceId,
    isSelected,
    lat,
    lng,
    cardActions,
    staticMapsURLs,
    isCatering
  } = props;
  const isMediumAndUp = useMediaQuery(theme => theme.breakpoints.up('md'));
  const isSmallAndUp = useMediaQuery(theme => theme.breakpoints.up('sm'));

  const locationLabelStyles = { variant: MENU_ITEM_NAMES };

  const locationDescriptionStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  const labelProps = {
    variant: ITEM_DESCRIPTION_AND_PRICES,
    ownState: ownTextState
  };

  const rootStaticMap = (
    <StaticMapComponent
      locations={[{ lat, lng }]}
      center={{ lat, lng }}
      name={name}
      isMediumAndUp={isMediumAndUp}
      isSmallAndUp={isSmallAndUp}
      staticUrls={staticMapsURLs}
      shouldLazyLoadMap
    />
  );

  return (
    <LocationsPaper isSelected={isSelected} ownState={ownCardState()}>
      <Flex>
        <StyledMap data-testid="location-map">
          <StyledMapCover>
            <InView triggerOnce>
              {({ inView, ref, entry }) => (
                <InterSectionObserver ref={ref} minHeight={MAP_BOX_MIN_HEIGHT}>
                  {inView && rootStaticMap}
                </InterSectionObserver>
              )}
            </InView>
          </StyledMapCover>
        </StyledMap>

        <StyledLocationCard>
          <StyledLocationInfo>
            <Box mb={0.75} display="flex">
              <LocationLabelTypography {...locationLabelStyles}>
                {name}
              </LocationLabelTypography>
              {distance && (
                <Box display="flex" alignItems="flex-end" ml={1}>
                  <MenuItemDescriptionTypography {...locationDescriptionStyles}>
                    ({distance})
                  </MenuItemDescriptionTypography>
                </Box>
              )}
            </Box>

            {street && city && (
              <Box mb={0.25}>
                <AddressLink
                  street={street}
                  city={city}
                  state={state}
                  postalCode={postalCode}
                  googlePlaceId={googlePlaceId}
                  dataTestId="location-address"
                />
              </Box>
            )}

            {phoneNumber && (
              <Box mb={0.75}>
                <PhoneAnchor
                  LabelProps={labelProps}
                  number={phoneNumber}
                  dataTestId="location-phone"
                />
              </Box>
            )}
            {description && (
              <Box mt={1} mb={0.5}>
                <MenuItemDescriptionTypography {...locationDescriptionStyles}>
                  {description}
                </MenuItemDescriptionTypography>
              </Box>
            )}
          </StyledLocationInfo>
          <StyledCardActionsSection isCatering={isCatering}>
            {cardActions}
          </StyledCardActionsSection>
        </StyledLocationCard>
      </Flex>
    </LocationsPaper>
  );
};

LocationsCard.propTypes = {
  isSelected: PropTypes.bool,
  isCatering: PropTypes.bool,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  distance: PropTypes.string,
  street: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  postalCode: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string,
  googlePlaceId: PropTypes.string,
  lat: PropTypes.string.isRequired,
  lng: PropTypes.string.isRequired,
  cardActions: CHILDREN_PROP_TYPES.isRequired,
  staticMapsURLs: PropTypes.object.isRequired,
  sx: PropTypes.object
};

LocationsCard.defaultProps = {
  firstAvailablePickupDateOption: '',
  firstAvailableDeliveryDateOption: '',
  googlePlaceId: '',
  phoneNumber: '',
  distance: '',
  description: '',
  name: '',
  isClosed: false,
  isSelected: false,
  isAsapPickupCurrentlyAvailable: true,
  isAsapDeliveryCurrentlyAvailable: true,
  doesDeliverToAddress: true,
  isAcceptingPickupOrders: true,
  isAcceptingDeliveryOrders: true,
  isCatering: false,
  sx: {}
};

export default LocationsCard;
